import { useState } from "react";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";

function clampScore(score: number) {
  return Math.min(5, Math.max(0, score));
}

function formatSkillScoreLabel(score?: number | null) {
  if (score == null) return "Sem score";
  const formatted = Number.isInteger(score) ? String(score) : score.toFixed(2).replace(".", ",");
  return `Score ${formatted}`;
}

export function SkillScoreStars({
  score,
  size = "md",
  className,
}: {
  score?: number | null;
  size?: "sm" | "md";
  className?: string;
}) {
  const clamped = score == null ? 0 : clampScore(score);
  const starSize = size === "sm" ? "text-[14px]" : "text-[18px]";

  return (
    <div
      className={cn("flex items-center justify-center gap-px", className)}
      role="img"
      aria-label={formatSkillScoreLabel(score)}
      title={formatSkillScoreLabel(score)}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const fill = Math.min(1, Math.max(0, clamped - (n - 1)));

        if (fill >= 1) {
          return (
            <Icon
              key={n}
              name="star"
              filled
              className={cn("leading-none text-amber-500", starSize)}
              aria-hidden
            />
          );
        }

        if (fill <= 0) {
          return (
            <Icon
              key={n}
              name="star"
              className={cn("leading-none text-muted-foreground/30", starSize)}
              aria-hidden
            />
          );
        }

        return (
          <span key={n} className={cn("relative inline-flex leading-none", starSize)} aria-hidden>
            <Icon name="star" className="text-muted-foreground/30" />
            <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
              <Icon name="star" filled className="text-amber-500" />
            </span>
          </span>
        );
      })}
    </div>
  );
}

export function MemberStarRating({
  value,
  disabled,
  onChange,
}: {
  value: number | null;
  disabled?: boolean;
  onChange: (stars: number) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const shown = hover ?? value ?? 0;
  return (
    <div className="flex items-center" role="group" aria-label="Classificação em estrelas (1 a 5)">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(null)}
          onClick={() => onChange(n)}
          className={cn(
            "p-0.5 rounded-md hover:bg-amber-500/10 transition-colors disabled:opacity-40 disabled:pointer-events-none",
          )}
          aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
        >
          <Icon
            name="star"
            filled={n <= shown}
            className={cn(
              "text-[22px] leading-none",
              n <= shown ? "text-amber-500" : "text-muted-foreground/30",
            )}
          />
        </button>
      ))}
    </div>
  );
}
